import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  FolderKanban,
  Tag,
  Settings,
  CircleDashed,
  Zap,
  Clock,
  Sparkles,
  Archive,
  ShieldAlert,
  Flag,
  GripVertical,
  Plus,
  Trash2,
  Check,
  ChevronDown,
  Layers,
} from 'lucide-react';
import clsx from 'clsx';
import { CustomFieldsManager } from '@/components/fields/CustomFieldsManager';
import { TagsManager } from '@/components/fields/TagsManager';
import { useModal } from '@/hooks/useModal';
import { clampToViewport } from '@/hooks/useDropdownPosition';
import { generateA11yId } from '@/utils/accessibility';
import { useOrganization } from '@/context/OrganizationContext';
import { getOrgMembers } from '@/db/organizations';
import type { OrgMember } from '@/db/organizations';
import { renameSpace } from '@/db/mutations';
import { handleError } from '@/utils/errorHandler';
import { toast } from '@/utils/toast';
import { fetchTaskStatuses, saveTaskStatuses } from '@/db/taskStatuses';
import { fetchStatusPresets, createStatusPreset, deleteStatusPreset, type StatusPreset } from '@/db/statusPresets';
import { useAuth } from '@/context/AuthContext';

// ─── Status icons / colors (same as ListSettingsModal) ────────────────────────

const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  CircleDashed, Zap, Clock, Sparkles, Archive, ShieldAlert, Flag,
};

const COLOR_OPTIONS = [
  { value: 'text-quaternary', label: 'Grigio', hex: '#6b7280' },
  { value: 'accent-blue', label: 'Blu', hex: '#3b82f6' },
  { value: 'accent-emerald', label: 'Verde', hex: '#10b981' },
  { value: 'accent-amber', label: 'Ambra', hex: '#f59e0b' },
  { value: 'accent-rose', label: 'Rosa', hex: '#f43f5e' },
  { value: 'accent-violet', label: 'Viola', hex: '#8b5cf6' },
  { value: 'accent-cyan', label: 'Ciano', hex: '#06b6d4' },
];

const STATUS_PRESETS: Record<string, { label: string; options: StatusOption[] }> = {
  default: {
    label: 'Standard',
    options: [
      { value: 'open', label: 'Aperto', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'in_review', label: 'In revisione', color: 'accent-amber', icon: 'Clock', category: 'active' },
      { value: 'completed', label: 'Completato', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
      { value: 'closed', label: 'Chiuso', color: 'accent-violet', icon: 'Archive', category: 'closed' },
    ],
  },
  kanban: {
    label: 'Kanban',
    options: [
      { value: 'backlog', label: 'Backlog', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'todo', label: 'To Do', color: 'accent-cyan', icon: 'Flag', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'in_review', label: 'In revisione', color: 'accent-amber', icon: 'Clock', category: 'active' },
      { value: 'done', label: 'Fatto', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
    ],
  },
  bug_tracking: {
    label: 'Bug Tracking',
    options: [
      { value: 'new', label: 'Nuovo', color: 'accent-rose', icon: 'ShieldAlert', category: 'open' },
      { value: 'triaged', label: 'Triaged', color: 'accent-amber', icon: 'Flag', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'fixed', label: 'Fixato', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
      { value: 'closed', label: 'Chiuso', color: 'accent-violet', icon: 'Archive', category: 'closed' },
    ],
  },
  scrum: {
    label: 'Scrum',
    options: [
      { value: 'backlog', label: 'Backlog', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'sprint_ready', label: 'Sprint Ready', color: 'accent-cyan', icon: 'Flag', category: 'open' },
      { value: 'in_progress', label: 'In Progress', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'testing', label: 'Testing', color: 'accent-amber', icon: 'Clock', category: 'active' },
      { value: 'done', label: 'Done', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
    ],
  },
  simple: {
    label: 'Semplice',
    options: [
      { value: 'todo', label: 'Da fare', color: 'text-quaternary', icon: 'CircleDashed', category: 'open' },
      { value: 'in_progress', label: 'In corso', color: 'accent-blue', icon: 'Zap', category: 'active' },
      { value: 'done', label: 'Fatto', color: 'accent-emerald', icon: 'Sparkles', category: 'closed' },
    ],
  },
};

const DEFAULT_PRESET = STATUS_PRESETS.default.options;

type StatusCategory = 'open' | 'active' | 'closed';
type StatusOption = { value: string; label: string; color: string; icon?: string; category?: StatusCategory };

const CATEGORY_OPTIONS: { value: StatusCategory; label: string; abbr: string; color: string }[] = [
  { value: 'open', label: 'Aperto', abbr: 'A', color: '#6b7280' },
  { value: 'active', label: 'In corso', abbr: 'C', color: '#3b82f6' },
  { value: 'closed', label: 'Chiuso', abbr: 'X', color: '#10b981' },
];

// ─── Preset dropdown ─────────────────────────────────────────────────────────

function PresetDropdown({ savedPresets, onSelect, onDeletePreset }: {
  savedPresets: StatusPreset[];
  onSelect: (opts: StatusOption[]) => void;
  onDeletePreset: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => {
          if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setMenuPos({ top: r.bottom + 4, left: r.left });
          }
          setOpen(v => !v);
        }}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-accent-violet hover:border-accent-violet/50 transition-all"
      >
        <ChevronDown className="w-3.5 h-3.5" />
        Usa preset
      </button>
      {open && (
        <div className="fixed z-[999] bg-surface-primary border border-border-primary rounded-lg shadow-2xl min-w-[220px] py-1 animate-fade-in" style={{ top: menuPos.top, left: menuPos.left }}>
          {savedPresets.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-text-quaternary font-semibold">Salvati</div>
              {savedPresets.map(p => (
                <div key={p.id} className="flex items-center group">
                  <button
                    onClick={() => { onSelect(p.statuses.map(s => ({ value: s.value, label: s.name, color: s.color ?? 'text-quaternary', icon: s.icon ?? undefined }))); setOpen(false); }}
                    className="flex-1 flex items-center gap-2 px-3 py-2 text-left text-[11px] text-text-primary hover:bg-surface-hover transition-colors"
                  >
                    <span className="font-medium truncate">{p.name}</span>
                    <span className="text-text-quaternary ml-auto shrink-0">{p.statuses.length} stati</span>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onDeletePreset(p.id); }}
                    className="p-1.5 mr-1 rounded opacity-0 group-hover:opacity-100 hover:bg-accent-rose/10 transition-all">
                    <Trash2 className="w-3 h-3 text-text-quaternary hover:text-accent-rose" />
                  </button>
                </div>
              ))}
              <div className="border-t border-border-primary my-1" />
            </>
          )}
          <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-text-quaternary font-semibold">Predefiniti</div>
          {Object.entries(STATUS_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => { onSelect(preset.options); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] text-text-primary hover:bg-surface-hover transition-colors"
            >
              <span className="font-medium">{preset.label}</span>
              <span className="text-text-quaternary ml-auto">{preset.options.length} stati</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Status row (reusable, same as in ListSettingsModal) ──────────────────────

function StatusOptionRow({
  option, onUpdate, onDelete, onDragStart, onDragOver, onDragEnd, index,
}: {
  option: StatusOption; index: number;
  onUpdate: (idx: number, updates: Partial<StatusOption>) => void;
  onDelete: (idx: number) => void;
  onDragStart: (idx: number) => void;
  onDragOver: (idx: number) => void;
  onDragEnd: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(option.label);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const colorBtnRef = useRef<HTMLButtonElement>(null);
  const colorHex = COLOR_OPTIONS.find((c) => c.value === option.color)?.hex ?? '#6b7280';
  const Icon = option.icon ? STATUS_ICONS[option.icon] : CircleDashed;

  const commitLabel = () => {
    const trimmed = label.trim();
    if (trimmed && trimmed !== option.label) {
      onUpdate(index, { label: trimmed, value: trimmed.toLowerCase().replace(/\s+/g, '_') });
    } else { setLabel(option.label); }
    setEditing(false);
  };

  const handleColorClick = () => {
    if (!showColorPicker && colorBtnRef.current) {
      const r = colorBtnRef.current.getBoundingClientRect();
      setPickerPos(clampToViewport(r.left, r.bottom + 4, 140, 200));
    }
    setShowColorPicker((v) => !v);
  };

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDragEnd={onDragEnd}
      className="group flex items-center gap-2 px-3 py-2 rounded-lg border border-border-primary bg-surface-tertiary/40 hover:border-border-secondary transition-colors"
    >
      <GripVertical className="w-3.5 h-3.5 text-text-quaternary opacity-0 group-hover:opacity-100 cursor-grab shrink-0" />
      <div className="shrink-0">
        <button
          ref={colorBtnRef}
          onClick={handleColorClick}
          className="flex items-center justify-center w-5 h-5 rounded-full hover:ring-2 hover:ring-border-secondary transition-all"
          style={{ backgroundColor: colorHex + '33' }}
          title="Cambia colore"
        >
          <span style={{ color: colorHex }}>{Icon && <Icon className="w-3 h-3" />}</span>
        </button>
        {showColorPicker && (
          <div
            className="fixed z-[999] p-2 rounded-lg border border-border-primary bg-surface-primary shadow-xl"
            style={{ top: pickerPos.top, left: pickerPos.left }}
          >
            <div className="grid grid-cols-4 gap-1.5 mb-2">
              {COLOR_OPTIONS.map((c) => (
                <button key={c.value} onClick={() => { onUpdate(index, { color: c.value }); setShowColorPicker(false); }}
                  className="w-5 h-5 rounded-full hover:scale-110 transition-transform" style={{ backgroundColor: c.hex }} title={c.label} />
              ))}
            </div>
            <div className="border-t border-border-primary pt-2">
              <p className="text-[9px] text-text-quaternary mb-1.5 uppercase tracking-wider">Icona</p>
              <div className="grid grid-cols-4 gap-1">
                {Object.entries(STATUS_ICONS).map(([name, Ico]) => (
                  <button key={name} onClick={() => { onUpdate(index, { icon: name }); setShowColorPicker(false); }}
                    className={clsx('p-1 rounded hover:bg-surface-hover transition-colors', option.icon === name && 'bg-accent-violet/10')} title={name}>
                    <Ico className="w-3.5 h-3.5 text-text-secondary" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {editing ? (
        <input ref={inputRef} value={label} onChange={(e) => setLabel(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setLabel(option.label); setEditing(false); } }}
          autoFocus className="flex-1 text-[12px] bg-transparent border-b border-accent-violet text-text-primary outline-none pb-0.5" />
      ) : (
        <span className="flex-1 text-[12px] text-text-primary cursor-text" onDoubleClick={() => setEditing(true)} title="Doppio click per rinominare">
          {option.label}
        </span>
      )}
      <button
        onClick={() => {
          const cats: StatusCategory[] = ['open', 'active', 'closed'];
          const currentIdx = cats.indexOf(option.category || 'open');
          const nextCat = cats[(currentIdx + 1) % cats.length];
          onUpdate(index, { category: nextCat });
        }}
        className="shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors"
        style={{
          backgroundColor: (CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.color || '#6b7280') + '20',
          color: CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.color || '#6b7280',
        }}
        title={`Categoria: ${CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.label}. Click per cambiare.`}
      >
        {CATEGORY_OPTIONS.find(c => c.value === (option.category || 'open'))?.label}
      </button>
      <button onClick={() => onDelete(index)} className="shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-accent-rose/10 transition-all" title="Elimina stato">
        <Trash2 className="w-3.5 h-3.5 text-text-quaternary hover:text-accent-rose" />
      </button>
    </div>
  );
}

// ─── Space statuses manager ───────────────────────────────────────────────────

function SpaceStatusesManager({ spaceId, orgId }: { spaceId?: string; orgId?: string }) {
  const { user } = useAuth();
  const [options, setOptions] = useState<StatusOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('text-quaternary');
  const [newIcon, setNewIcon] = useState('CircleDashed');
  const [showNewColorPicker, setShowNewColorPicker] = useState(false);
  const [newPickerPos, setNewPickerPos] = useState({ top: 0, left: 0 });
  const newColorBtnRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!spaceId) { setLoading(false); return; }
    setLoading(true);
    let loadedFromDefaults = false;
    try {
      const rows = await fetchTaskStatuses('project', spaceId);
      if (rows.length > 0) {
        setOptions(rows.map((r) => ({ value: r.value, label: r.name, color: r.color ?? 'text-quaternary', icon: r.icon ?? undefined, category: (r as unknown as { category?: StatusCategory }).category || 'open' })));
      } else {
        setOptions(DEFAULT_PRESET);
        loadedFromDefaults = true;
      }
    } catch (e) {
      handleError(e, { level: 'warning', message: 'Errore caricamento stati' });
    } finally {
      setLoading(false);
      setDirty(loadedFromDefaults);
    }
  }, [spaceId]);

  useEffect(() => { load(); }, [load]);

  const save = async (newOptions: StatusOption[]) => {
    if (!spaceId) return;
    setSaving(true);
    try {
      await saveTaskStatuses(
        newOptions.map((o) => ({ value: o.value, name: o.label, color: o.color, icon: o.icon, category: o.category || 'open' })),
        'project',
        spaceId
      );
      setOptions(newOptions);
      setDirty(false);
      toast('Stati salvati', 'success');
    } catch (e) {
      handleError(e, { level: 'error', message: 'Errore nel salvataggio stati' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = (idx: number, updates: Partial<StatusOption>) => {
    setOptions((prev) => prev.map((o, i) => i === idx ? { ...o, ...updates } : o));
    setDirty(true);
  };

  const handleDelete = (idx: number) => {
    setOptions((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleDragOver = (idx: number) => {
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...options];
    const [removed] = next.splice(dragIdx, 1);
    next.splice(idx, 0, removed);
    setOptions(next);
    setDragIdx(idx);
    setDirty(true);
  };

  const handleAddStatus = () => {
    if (!newLabel.trim()) return;
    setOptions((prev) => [
      ...prev,
      { value: newLabel.trim().toLowerCase().replace(/\s+/g, '_'), label: newLabel.trim(), color: newColor, icon: newIcon },
    ]);
    setDirty(true);
    setNewLabel('');
    setNewColor('text-quaternary');
    setNewIcon('CircleDashed');
    setShowAddForm(false);
  };

  // Saved presets
  const [savedPresets, setSavedPresets] = useState<StatusPreset[]>([]);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');

  const loadSavedPresets = useCallback(async () => {
    if (!orgId) return;
    try { setSavedPresets(await fetchStatusPresets(orgId)); } catch { /* ignore */ }
  }, [orgId]);

  useEffect(() => { loadSavedPresets(); }, [loadSavedPresets]);

  const handleSavePreset = async () => {
    if (!presetName.trim() || !orgId) return;
    try {
      await createStatusPreset(orgId, presetName.trim(), options.map(o => ({ value: o.value, name: o.label, color: o.color, icon: o.icon })), user?.id);
      toast('Preset salvato', 'success');
      setShowSavePreset(false);
      setPresetName('');
      await loadSavedPresets();
    } catch { toast('Errore nel salvataggio del preset', 'error'); }
  };

  const handleDeletePreset = async (presetId: string) => {
    try {
      await deleteStatusPreset(presetId);
      setSavedPresets(prev => prev.filter(p => p.id !== presetId));
      toast('Preset eliminato', 'success');
    } catch { toast('Errore', 'error'); }
  };

  if (!spaceId) return <div className="text-center py-8 text-[11px] text-text-tertiary">Nessuno spazio selezionato.</div>;
  if (loading) return <div className="text-center py-8 text-[11px] text-text-quaternary">Caricamento…</div>;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-[13px] font-semibold text-text-primary">Stati dello spazio</h3>
        <p className="text-[11px] text-text-tertiary mt-0.5">
          Definisci il workflow predefinito per le liste di questo spazio. Trascina per riordinare, doppio click per rinominare.
        </p>
      </div>

      <div className="space-y-1.5">
        {options.map((opt, idx) => (
          <StatusOptionRow key={opt.value + idx} option={opt} index={idx}
            onUpdate={handleUpdate} onDelete={handleDelete}
            onDragStart={setDragIdx} onDragOver={handleDragOver} onDragEnd={() => setDragIdx(null)} />
        ))}
      </div>

      {showAddForm ? (
        <div className="p-3 rounded-lg border border-accent-violet/30 bg-accent-violet/5">
          <div className="flex items-center gap-2">
            <div>
              <button
                ref={newColorBtnRef}
                onClick={() => {
                  if (!showNewColorPicker && newColorBtnRef.current) {
                    const r = newColorBtnRef.current.getBoundingClientRect();
                    setNewPickerPos({ top: r.bottom + 4, left: r.left });
                  }
                  setShowNewColorPicker((v) => !v);
                }}
                className="flex items-center justify-center w-7 h-7 rounded-full border border-border-primary hover:border-accent-violet"
                style={{ backgroundColor: (COLOR_OPTIONS.find((c) => c.value === newColor)?.hex ?? '#6b7280') + '33' }}>
                {(() => { const Ico = STATUS_ICONS[newIcon]; const col = COLOR_OPTIONS.find((c) => c.value === newColor)?.hex; return <span style={{ color: col }}><Ico className="w-3.5 h-3.5" /></span>; })()}
              </button>
              {showNewColorPicker && (
                <div
                  className="fixed z-[999] p-2 rounded-lg border border-border-primary bg-surface-primary shadow-xl"
                  style={{ top: newPickerPos.top, left: newPickerPos.left }}
                >
                  <div className="grid grid-cols-4 gap-1.5 mb-2">
                    {COLOR_OPTIONS.map((c) => (
                      <button key={c.value} onClick={() => setNewColor(c.value)}
                        className={clsx('w-5 h-5 rounded-full hover:scale-110 transition-transform', newColor === c.value && 'ring-2 ring-white ring-offset-1')}
                        style={{ backgroundColor: c.hex }} />
                    ))}
                  </div>
                  <div className="border-t border-border-primary pt-2">
                    <p className="text-[9px] text-text-quaternary mb-1 uppercase tracking-wider">Icona</p>
                    <div className="grid grid-cols-4 gap-1">
                      {Object.entries(STATUS_ICONS).map(([name, Ico]) => (
                        <button key={name} onClick={() => { setNewIcon(name); setShowNewColorPicker(false); }}
                          className={clsx('p-1 rounded hover:bg-surface-hover', newIcon === name && 'bg-accent-violet/10')}>
                          <Ico className="w-3.5 h-3.5 text-text-secondary" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddStatus(); if (e.key === 'Escape') setShowAddForm(false); }}
              placeholder="Nome stato…" autoFocus
              className="flex-1 px-2 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet" />
            <button onClick={handleAddStatus} disabled={!newLabel.trim()}
              className="px-3 py-1.5 rounded-lg bg-accent-violet text-white text-[11px] font-medium disabled:opacity-40">Aggiungi</button>
            <button onClick={() => setShowAddForm(false)} className="p-1.5 rounded hover:bg-surface-hover text-text-quaternary">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-text-secondary hover:border-text-tertiary transition-all">
            <Plus className="w-3.5 h-3.5" />
            Aggiungi stato
          </button>
          <PresetDropdown
            savedPresets={savedPresets}
            onSelect={(opts) => { setOptions(opts); setDirty(true); }}
            onDeletePreset={handleDeletePreset}
          />
          {options.length > 0 && !showSavePreset && (
            <button
              onClick={() => setShowSavePreset(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-accent-emerald hover:border-accent-emerald/50 transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              Salva come preset
            </button>
          )}
          {showSavePreset && (
            <div className="flex items-center gap-2">
              <input value={presetName} onChange={e => setPresetName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); if (e.key === 'Escape') setShowSavePreset(false); }}
                placeholder="Nome preset..." autoFocus
                className="px-2 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-[11px] text-text-primary focus:border-accent-emerald focus:ring-1 focus:ring-accent-emerald w-36" />
              <button onClick={handleSavePreset} disabled={!presetName.trim()}
                className="px-2.5 py-1.5 rounded-lg bg-accent-emerald text-white text-[11px] font-medium disabled:opacity-40 hover:bg-accent-emerald/90">Salva</button>
              <button onClick={() => setShowSavePreset(false)} className="p-1 rounded hover:bg-surface-hover text-text-quaternary">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {dirty && (
            <button onClick={() => save(options)} disabled={saving}
              className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent-violet text-white text-[11px] font-medium hover:bg-accent-violet/90 disabled:opacity-60">
              <Check className="w-3.5 h-3.5" />
              {saving ? 'Salvo…' : 'Salva stati'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface SpaceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceName: string;
  spaceId?: string;
}

export function SpaceSettingsModal({
  isOpen,
  onClose,
  spaceName,
  spaceId,
}: SpaceSettingsModalProps) {
  const { activeTab, setActiveTab } = useModal<'general' | 'stati' | 'tag' | 'campi'>(isOpen, onClose, 'general');
  const { currentOrg } = useOrganization();

  const [name, setName] = useState(spaceName);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const modalLabelId = useMemo(() => generateA11yId('space-settings-modal-label'), []);
  const modalDescId = useMemo(() => generateA11yId('space-settings-modal-desc'), []);

  useEffect(() => {
    if (!isOpen || !currentOrg?.id) { setMembers([]); return; }
    let cancelled = false;
    setMembersLoading(true);
    getOrgMembers(currentOrg.id)
      .then((data) => { if (!cancelled) setMembers(data); })
      .catch((err) => { handleError(err, { level: 'warning', message: 'Errore nel caricamento dei membri' }); if (!cancelled) setMembers([]); })
      .finally(() => { if (!cancelled) setMembersLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, currentOrg?.id]);

  useEffect(() => { setName(spaceName); }, [spaceName]);

  const handleNameBlur = async () => {
    const trimmed = name.trim();
    if (!spaceId || trimmed === spaceName || !trimmed) { setName(spaceName); return; }
    try {
      await renameSpace(spaceId, trimmed);
      toast('Nome spazio aggiornato', 'success');
    } catch (err) {
      handleError(err, { level: 'error', message: 'Impossibile rinominare lo spazio' });
      setName(spaceName);
    }
  };

  if (!isOpen) return null;

  const getInitials = (member: OrgMember) => {
    if (member.display_name) return member.display_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
    if (member.email) return member.email.slice(0, 2).toUpperCase();
    return '??';
  };

  const getRoleLabel = (role: OrgMember['role']) => {
    switch (role) {
      case 'owner': return 'Owner';
      case 'admin': return 'Admin';
      case 'member': return 'Membro';
      default: return role;
    }
  };

  const headerTitle =
    activeTab === 'general' ? 'Impostazioni Spazio' :
    activeTab === 'stati' ? 'Stati predefiniti' :
    activeTab === 'tag' ? 'Tag' :
    'Campi personalizzati';

  const headerSub =
    activeTab === 'general' ? spaceName :
    activeTab === 'stati' ? `Workflow predefinito per le liste di ${spaceName}` :
    activeTab === 'tag' ? `Tag disponibili in ${spaceName}` :
    `Campi personalizzati per ${spaceName}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalLabelId}
        aria-describedby={modalDescId}
        className="relative w-full max-w-3xl max-h-[85vh] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in flex overflow-hidden"
      >
        {/* Sidebar */}
        <div className="w-52 bg-surface-tertiary/50 border-r border-border-primary p-3 flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 mb-4">
            <FolderKanban className="w-5 h-5 text-text-tertiary" />
            <span className="text-[14px] font-semibold text-text-primary">Impostazioni</span>
          </div>

          <nav className="space-y-1" role="tablist" aria-label="Schede impostazioni spazio">
            {([
              { id: 'general', label: 'Generale', icon: Settings },
              { id: 'stati', label: 'Stati', icon: CircleDashed },
              { id: 'tag', label: 'Tag', icon: Tag },
              { id: 'campi', label: 'Campi', icon: Layers },
            ] as const).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                role="tab"
                aria-selected={activeTab === id}
                onClick={() => setActiveTab(id)}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all',
                  activeTab === id
                    ? 'bg-accent-violet/10 text-accent-violet'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
            <div>
              <h2 id={modalLabelId} className="text-[15px] font-semibold text-text-primary">{headerTitle}</h2>
              <p className="text-[11px] text-text-tertiary">{headerSub}</p>
            </div>
            <button onClick={onClose} aria-label="Chiudi impostazioni spazio"
              className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-tertiary">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div id={modalDescId} className="sr-only">
            Impostazioni per la personalizzazione dello spazio e gestione di stati, tag e campi personalizzati
          </div>

          <div className="flex-1 overflow-y-auto p-6" role="tabpanel" id={`space-settings-panel-${activeTab}`} aria-labelledby={modalLabelId}>
            {activeTab === 'general' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-[13px] font-semibold text-text-primary mb-1">Informazioni spazio</h3>
                  <p className="text-[11px] text-text-tertiary mb-4">Configura le informazioni base dello spazio</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[11px] font-medium text-text-secondary mb-1.5">Nome spazio</label>
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)} onBlur={handleNameBlur}
                        className="w-full px-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary placeholder:text-text-quaternary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet transition-all" />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-[13px] font-semibold text-text-primary mb-1">Membri del team</h3>
                  <p className="text-[11px] text-text-tertiary mb-4">Gestisci i membri con accesso allo spazio</p>
                  {membersLoading ? (
                    <div className="flex items-center gap-2 p-3 text-[11px] text-text-tertiary">
                      <div className="w-4 h-4 border-2 border-accent-violet/30 border-t-accent-violet rounded-full animate-spin" />
                      Caricamento membri...
                    </div>
                  ) : members.length > 0 ? (
                    <div className="space-y-2">
                      {members.map((member) => (
                        <div key={member.id} className="flex items-center gap-3 p-3 rounded-lg bg-surface-tertiary/50 border border-border-primary">
                          <div className="w-8 h-8 rounded-full bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center overflow-hidden flex-shrink-0">
                            {member.avatar_url
                              ? <img src={member.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                              : <span className="text-[10px] font-bold text-white">{getInitials(member)}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-medium text-text-primary">{member.display_name || member.email || 'Utente'}</p>
                            {member.email && member.display_name && <p className="text-[10px] text-text-quaternary">{member.email}</p>}
                          </div>
                          <span className={clsx(
                            'px-2 py-0.5 rounded-full text-[10px] font-medium',
                            member.role === 'owner' ? 'bg-accent-amber/10 text-accent-amber' :
                            member.role === 'admin' ? 'bg-accent-violet/10 text-accent-violet' :
                            'bg-surface-active text-text-tertiary'
                          )}>
                            {getRoleLabel(member.role)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-text-quaternary p-3">Nessun membro trovato</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'stati' && <SpaceStatusesManager spaceId={spaceId} orgId={currentOrg?.id} />}

            {activeTab === 'tag' && (
              <TagsManager
                scope="project"
                scopeId={spaceId}
                orgId={currentOrg?.id}
                orgName={currentOrg?.name}
              />
            )}

            {activeTab === 'campi' && (
              <CustomFieldsManager scope="project" scopeId={spaceId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
